/**
 * User Invitations API Service
 *
 * Service for managing user invitation-based onboarding
 * Integrates with backend invitations endpoints
 */
import api from "./api";

/**
 * Invitation types
 */
export interface InvitationCreate {
  email: string;
  first_name?: string;
  last_name?: string;
  telefone?: string;
  cpf?: string;
  usuario_dominio?: string;
  org_category_id?: string;
  initial_roles?: string[]; // Array of role UUIDs
  metadata?: Record<string, any>;
}

export interface InvitationResponse {
  id: string;
  token: string;
  token_expires_at: string;
  status: "pendente" | "aceito" | "expirado" | "cancelado";
  email: string;
  first_name?: string;
  last_name?: string;
  telefone?: string;
  cpf?: string;
  usuario_dominio?: string;
  convidado_por: string;
  convidado_em: string;
  aceito_em?: string;
  user_id?: string;
  company_id: string;
  org_category_id?: string;
  initial_roles: string[];
  created_at: string;
}

export interface InvitationListResponse {
  invitations: InvitationResponse[];
  total_count: number;
}

export interface InvitationValidateResponse {
  valid: boolean;
  error?: string;
  invitation?: {
    email: string;
    first_name?: string;
    last_name?: string;
    company_id: string;
    expires_at: string;
  };
}

export interface InvitationActivateRequest {
  token: string;
  password: string;
  password_confirmation: string;
}

export interface InvitationActivateResponse {
  user_id: string;
  email: string;
  message: string;
  access_token?: string;
  refresh_token?: string;
}

export interface InvitationResendResponse {
  invitation_id: string;
  email: string;
  new_token_expires_at: string;
  message: string;
}

/**
 * Invitations API
 */
export const invitationsApi = {
  /**
   * Create new invitation (Admin only)
   *
   * Generates unique token and sends email to invitee.
   *
   * @param data - Invitation creation data
   * @returns Created invitation with token
   */
  create: async (data: InvitationCreate): Promise<InvitationResponse> => {
    const response = await api.post<InvitationResponse>("/users/invite", data);
    return response.data;
  },

  /**
   * Validate invitation token (Public endpoint)
   *
   * Checks if token is valid and not expired.
   * Used by activation page to verify token before showing form.
   *
   * @param token - Invitation token from URL
   * @returns Validation result with invitation details if valid
   */
  validate: async (token: string): Promise<InvitationValidateResponse> => {
    const response = await api.get<InvitationValidateResponse>(`/users/invite/${token}`);
    return response.data;
  },

  /**
   * Activate user account (Public endpoint)
   *
   * Creates new user, assigns roles, links to org category, marks invitation as accepted.
   *
   * @param data - Activation data (token + password)
   * @returns User info and auth tokens
   */
  activate: async (data: InvitationActivateRequest): Promise<InvitationActivateResponse> => {
    const response = await api.post<InvitationActivateResponse>("/users/activate", data);
    return response.data;
  },

  /**
   * Resend invitation (Admin only)
   *
   * Regenerates token and extends expiration.
   * Only works for pendente invitations.
   *
   * @param invitationId - Invitation ID
   * @returns Updated invitation info
   */
  resend: async (invitationId: string): Promise<InvitationResendResponse> => {
    const response = await api.post<InvitationResendResponse>(
      `/users/invite/${invitationId}/resend`
    );
    return response.data;
  },

  /**
   * Cancel invitation (Admin only)
   *
   * Marks invitation as "cancelado".
   * Cannot cancel already accepted invitations.
   *
   * @param invitationId - Invitation ID
   */
  cancel: async (invitationId: string): Promise<void> => {
    await api.delete(`/users/invite/${invitationId}`);
  },

  /**
   * List all invitations (Admin only)
   *
   * Get all invitations for current company with optional filters.
   *
   * @param filters - Optional filters (status, email)
   * @returns List of invitations
   */
  list: async (filters?: {
    status?: "pendente" | "aceito" | "expirado" | "cancelado";
    email?: string;
  }): Promise<InvitationListResponse> => {
    const params: Record<string, string> = {};

    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.email) {
      params.email = filters.email;
    }

    const response = await api.get<InvitationListResponse>("/users/invitations", { params });
    return response.data;
  },
};

/**
 * Export default for convenience
 */
export default invitationsApi;
